import { Routes, Route, Navigate } from "react-router-dom"
import React from "react"
import {
  ROOT,
  LOGIN,
  BOOKING_HISTORY,
  CENTERS,
  ADD_CENTER,
  MEETING_ROOMS,
  ADD_MEETING_ROOM,
  USERS,
  CENTER_DETAILS,
  AUDIT_LOGS,
  USER_PROFILE,
  TRANSACTIONS,
  USER_BOOKING_DETAILS,
  USER_AUDIT_LOG,
  BANK_ACCOUNTS,
  BANK_ACCOUNTS_VIEW,
  BOOKING_HISTORY_DETAILS,
  USER_BOOKING_DETAILS_BY_ID,
  INVOICES,
  CATEGORIES,
  CATEGORY_1,
  CATEGORY_2,
  CATEGORY_3,
  TRANSACTIONS_UPLOAD_HISTORY,
  CUSTOMERS,
  CUSTOMER_REPOSITORY,
} from "../constants/path"
import Login from "../containers/Login"
import BookingHistory from "../containers/BookingHistory"
import Centers from "../containers/Centers"
import MeetingRooms from "../containers/MeetingRooms"
import AddCenterForm from "../containers/AddCenterForm"
import AddMeetingRoomForm from "../containers/AddMeetingRoomForm"
import Users from "../containers/Users"
import CenterDetails from "../containers/CenterDetails"
import AuditLogs from "../containers/AuditLogs"
import {
  allowedOnlyBankAccountsRoles,
  allowedOnlyTrasactionsRoles,
  UserRole,
} from "../constants/data"
import { NotAuthorized, NotFound } from "../components/NotFound/NotFound"
import { Transactions } from "../containers/Transactions"
import UserProfile from "../containers/UserProfile"
import { BankAccounts } from "../containers/BankAccounts"
import AddBankAccount from "../containers/AddBankAccount"
import BookingHistoryDetails from "../containers/BookingHistoryDetails"
import Invoices from "../containers/Invoices"
import { Categories } from "../containers/Categories"
import UploadHistory from "../containers/UploadHistory"
import { Customers } from "../containers/Customers"
import { CustomerRepository } from "../containers/CustomerRepository"

type ProtectedRouteProps = {
  children: React.ReactNode
  token: string | null
  role: string
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  token,
  role,
}: ProtectedRouteProps) => {
  if (!token) {
    // user is not authenticated
    return <Navigate to={LOGIN} />
  }
  if (role === UserRole.ADMIN) {
    return <>{children}</>
  }
  const currentPath = window.location.pathname

  if (role === UserRole.AUDITOR) {
    const restrictedKeywords = ["add", TRANSACTIONS, BANK_ACCOUNTS]
    const isRestricted = restrictedKeywords.some((keyword) =>
      currentPath.includes(keyword),
    )
    if (isRestricted) {
      return <NotAuthorized />
    }
    return <>{children}</>
  }
  let ReturnVal = <NotAuthorized />

  if (allowedOnlyBankAccountsRoles.includes(role)) {
    const allowedBankKeyword = [BANK_ACCOUNTS]

    const isBankAllowed = allowedBankKeyword.some((keyword) =>
      currentPath.includes(keyword),
    )

    if (isBankAllowed) {
      ReturnVal = <>{children}</>
    }
  }

  if (allowedOnlyTrasactionsRoles.includes(role)) {
    const allowedTransactionKeyword = [
      TRANSACTIONS,
      CATEGORIES,
      CUSTOMER_REPOSITORY,
      CUSTOMERS,
    ]
    const isTransactionAllowed = allowedTransactionKeyword.some((keyword) =>
      currentPath.includes(keyword),
    )

    if (isTransactionAllowed) {
      ReturnVal = <>{children}</>
    }
  }

  return ReturnVal
}

const routes = [
  { path: BOOKING_HISTORY, element: <BookingHistory /> },
  { path: BOOKING_HISTORY_DETAILS, element: <BookingHistoryDetails /> },
  { path: USER_BOOKING_DETAILS_BY_ID, element: <BookingHistoryDetails /> },
  { path: USER_PROFILE, element: <UserProfile /> },
  { path: USER_BOOKING_DETAILS, element: <UserProfile /> },
  { path: USER_AUDIT_LOG, element: <UserProfile /> },
  { path: CENTERS, element: <Centers /> },
  { path: ADD_CENTER, element: <AddCenterForm /> },
  { path: CENTER_DETAILS, element: <CenterDetails /> },
  { path: MEETING_ROOMS, element: <MeetingRooms /> },
  { path: ADD_MEETING_ROOM, element: <AddMeetingRoomForm /> },
  { path: USERS, element: <Users /> },
  { path: AUDIT_LOGS, element: <AuditLogs /> },
  { path: TRANSACTIONS, element: <Transactions /> },
  { path: CUSTOMERS, element: <Customers /> },
  { path: CUSTOMER_REPOSITORY, element: <CustomerRepository /> },
  { path: TRANSACTIONS_UPLOAD_HISTORY, element: <UploadHistory /> },
  { path: BANK_ACCOUNTS, element: <BankAccounts /> },
  { path: BANK_ACCOUNTS_VIEW, element: <AddBankAccount /> },
  { path: INVOICES, element: <Invoices /> },
  { path: CATEGORIES, element: <Categories /> },
  { path: CATEGORY_1, element: <Categories /> },
  { path: CATEGORY_2, element: <Categories /> },
  { path: CATEGORY_3, element: <Categories /> },
]

type AppRoutesProps = {
  token: string | null
  role: string
}

const AppRoutes: React.FC<AppRoutesProps> = ({
  token,
  role,
}: AppRoutesProps) => {
  return (
    <Routes>
      <Route path={ROOT} element={<Navigate to={LOGIN} />} />
      <Route path={LOGIN} element={<Login />} />
      {routes.map((route) => (
        <Route
          key={route.path}
          path={route.path}
          element={
            <ProtectedRoute token={token} role={role}>
              {route.element}
            </ProtectedRoute>
          }
        />
      ))}
      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}

export default AppRoutes
